import React, { Fragment, useEffect, useState } from 'react';
import { bool, func, string, object, number, arrayOf } from 'prop-types';
import get from 'lodash/get';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
} from '@material-ui/core';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { TablePagination } from '../../../../components';
import SingleRow from './SingleRow';
import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import {
  deleteKey,
  regenerateSecret,
} from '../../../../actions/application';
import {
  KEY_NAME_LABEL,
  STATUS_LABEL,
  DAYS_TO_KEY_EXPIRY_LABEL,
} from '../labels';
import styles from './styles';
import { ALERT_ERROR, ALERT_SUCCESS, API_KEY_STATUS_DELETE_FAILED } from '../../../../constants';
import EditKey from '../EditKey';
import {
  getDeleteKeyErrors,
  getDeleteKeyStatus,
  getRegeneratedSecretDetails,
  getRegerateSecretStatus,
} from '../../../../reducers/application';

const GRID_ROWS_PER_PAGE_OPTIONS = [10, 20];
const GRID_ROWS_PER_PAGE_DEFAULT_OPTION = 10;

export const ConfigurationRaw = ({
  applicationUuid,
  applicationDetails,
  fetchApplicationKeys,
  appKeys,
  appKeysTotalPages,
  appKeysTotalElements,
  isMultiKeySupport,
  classes,
  notifyMessages,
  isAdmin,
  appSecretHashingMetadata,
  locked,
  isLocking,
  disabledByInternal,
  onSaveDone,
  deleteKey,
  apiKeySettings,
  regenerateSecret,
  regeneratedSecretDetails,
  regerateSecretStatus,
  isEditDisabled,
  isPortalAdmin,
  deleteKeyStatus,
  deleteKeyErrors,
}) => {
  const intl = getI18n(useIntl());
  const rows = isMultiKeySupport ? appKeys : [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [isEditKeyOpen, setIsEditKeyOpen] = useState(false);
  const [expandIndex, setExpandIndex] = useState(0);
  const [defaultHashType, setDefaultHashType] = useState('');

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchApplicationKeys(applicationUuid, newPage, rowsPerPage);
  };

  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };
  const addNewKey = () => {
    setIsEditKeyOpen(true);
  };

  const isApiKeyExpiryEnabled = get(apiKeySettings, 'enabled', false);
  const startReportingDays = get(apiKeySettings, 'startReportingBeforeDays', 90);
  const urgentReportingDays = get(apiKeySettings, 'reportAsUrgentBeforeDays', 60);

  useEffect(() => {
    if (!get(appSecretHashingMetadata, 'plaintextAllowed')) {
      if (get(appSecretHashingMetadata, 'algorithm')) {
        setDefaultHashType('HASHED_SECRET');
      } else {
        setDefaultHashType('PLAIN_SECRET');
      }
    } else {
      setDefaultHashType('HASHED_SECRET');
    }
  }, [appSecretHashingMetadata]);

  useEffect(() => {
    if(isAdmin && appKeys[expandIndex] &&
       appKeys[expandIndex].status === API_KEY_STATUS_DELETE_FAILED) {
      if(deleteKeyStatus === 'SUCCESS') {
        notifyMessages(intl.getI18nMessage('label.application.details.keys.key.delete.success'), ALERT_SUCCESS, true);
      } else if (deleteKeyStatus === 'FAIL') {
        if (deleteKeyErrors.length > 0 && (deleteKeyErrors[0].field === 'apiKey' || deleteKeyErrors[0].field === 'proxyCheck')) {
          notifyMessages(deleteKeyErrors[0].error, ALERT_ERROR, true);
        } else {
          notifyMessages(intl.getI18nMessage('label.application.details.keys.key.delete.error'), ALERT_ERROR, true);
        }
      }
    }
  },[deleteKeyStatus]);

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchApplicationKeys(applicationUuid, 0, newRowsPerPage);
  };

  const addKeyDetails = {
    name : null,
    status : 'ENABLED',
    defaultKey : false,
    callbackUrl : null,
    oauthCallbackUrl: null,
    oauthScope : null,
    oauthType : 'PUBLIC',
    apiKey : '',
    keySecret : '',
    isHashed : defaultHashType === 'HASHED_SECRET',
    isRegenerateSecret : false,
  }

  return (
    <div id="app-configuration-container" className={classes.appConfigurationContainer}>
        <Fragment>
          <Dialog
            open={isEditKeyOpen}
            onClose={() => setIsEditKeyOpen(false)}
            aria-labelledby="edit-key-dialog"
            id={"new-api-key"}
            classes={{
              paper: classes.paperClass,
            }}
          >
            <EditKey
              applicationUuid={applicationUuid}
              details={addKeyDetails}
              closeAction={() => {
                setIsEditKeyOpen(false);
                fetchApplicationKeys(applicationUuid);
              }}
              appSecretHashingMetadata={appSecretHashingMetadata}
              notifyMessages={notifyMessages}
              keyCount={appKeysTotalElements}
              onSaveDone={onSaveDone}
              isPortalAdmin={isPortalAdmin}
            />
          </Dialog>
          <div className={classes.filtersAndAdd}>
            {(isAdmin || appKeysTotalElements === 0) &&
              <Button
                id="add-key-button"
                data-apim-test="add-key-button"
                variant="contained"
                color="secondary"
                className={classes.addKeyButton}
                onClick={addNewKey}
                disabled={(appKeysTotalElements === 1 && applicationDetails.status === 'INCOMPLETE')
                                        || isEditDisabled}
              >
                {getI18nFormattedMessage('label.application.details.keys.addkey.button')}
              </Button>
            }
          </div>
          
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="collapsible table">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>{KEY_NAME_LABEL}</TableCell>
                  <TableCell>{STATUS_LABEL}</TableCell>
                  {isApiKeyExpiryEnabled && <TableCell>{DAYS_TO_KEY_EXPIRY_LABEL}</TableCell>}
                  <TableCell className={classes.expanderColumn}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows && rows.length > 0 ? rows.map((row, idx) =>
                  <SingleRow
                    key={idx}
                    row={row}
                    idx={idx}
                    classes={classes}
                    notifyMessages={notifyMessages}
                    isAdmin={isAdmin}
                    appSecretHashingMetadata={appSecretHashingMetadata}
                    applicationUuid={applicationUuid}
                    isLocking={isLocking}
                    isApiKeyExpiryEnabled={isApiKeyExpiryEnabled}
                    startReportingDays={startReportingDays}
                    urgentReportingDays={urgentReportingDays}
                    locked={locked}
                    expandIndex={expandIndex}
                    setExpandIndex={setExpandIndex}
                    disabledByInternal={disabledByInternal}
                    onSaveDone={onSaveDone}
                    deleteKey={deleteKey}
                    regenerateSecret={regenerateSecret}
                    regeneratedSecretDetails={regeneratedSecretDetails}
                    regerateSecretStatus={regerateSecretStatus}
                    defaultHashType={defaultHashType}
                    isEditDisabled={isEditDisabled}
                    fetchApplicationKeys={fetchApplicationKeys}
                    isPortalAdmin={isPortalAdmin}
                  />)
                :<span>{getI18nFormattedMessage('label.application.details.keys.overview.nokeys')}</span>
                }
              </TableBody>
            </Table>
          </TableContainer>
          <div className={classes.paginationContainer}>
            <TablePagination
              id="app-configuration-keys-pagination"
              data-apim-test="app-configuration-keys-pagination"
              page={page}
              count={appKeysTotalElements}
              pageCount={appKeysTotalPages}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onChangePage={onChangePage}
              onChangePreviousPage={onChangePreviousPage}
              onChangeNextPage={onChangeNextPage}
              labelRowsPerPage={intl.getI18nMessage('label.pagination.rows.per.page')}
            />
          </div>
        </Fragment>
    </div>
  );
};

const mapStateToProps = state => ({
  regeneratedSecretDetails: getRegeneratedSecretDetails(state),
  regerateSecretStatus: getRegerateSecretStatus(state),
  deleteKeyStatus: getDeleteKeyStatus(state),
  deleteKeyErrors: getDeleteKeyErrors(state),
});

const mapDispatchToProps = {
  regenerateSecret,
  deleteKey,
};
ConfigurationRaw.propTypes = {
  applicationUuid: string,
  applicationDetails: object,
  fetchApplicationKeys: func,
  apiKeySettings: object,
  appKeys: arrayOf(object),
  appKeysTotalPages: number,
  appKeysTotalElements: number,
  isMultiKeySupport: bool,
  classes: object,
  isAdmin: bool,
  notifyMessages: func,
  appSecretHashingMetadata: object,
  isLocking: bool,
  locked: bool,
  disabledByInternal: bool,
  onSaveDone: func,
  regenerateSecret: func,
  regeneratedSecretDetails: object,
  regerateSecretStatus: string,
  deleteKey: func,
  isEditDisabled: bool,
  isPortalAdmin: bool,
  deleteKeyStatus: func,
  deleteKeyErrors: arrayOf(object),
};
ConfigurationRaw.displayName = 'Configuration';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ConfigurationRaw);
